import { getElectionElectoratesByIds } from '@/apis/electionElectorates';
import { getElectionById } from '@/apis/elections';
import { ListManagerFrontendError } from '@/errors';
import { isTemplatePlaceholder, RosterTypes } from '@/types';
import { RosterFilter } from '@/types/searchFilterTypes';
import { ROSTER_TYPES_WITH_IDS, RosterTypeRecordResult } from '@/constants/filterCriteria/roster';
import { ElectorateRecordResult } from '@/types/filterCriteriaRecords';
import { FilterMapper, ValidationContext } from './filterMapperTypes';

export const RosterMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const isNoElectionSelected = value[0].election === null;
    return isNoElectionSelected;
  },

  toFilter({ value }): RosterFilter {
    if (!value) throw new Error('Missing value');
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    // The electionId must have a value in a valid RosterFilter - rather than
    // changing the type we put -1 in it to represent a missing election.
    const mappedElectionId = isTemplatePlaceholder(value[0].election)
      ? value[0].election
      : (value[0].election?.id ?? -1);

    const mappedElectorateIds = isTemplatePlaceholder(value[0].electorates)
      ? value[0].electorates
      : (value[0].electorates as ElectorateRecordResult[]).map(({ id }) => id);

    const mappedRosterTypes = isTemplatePlaceholder(value[0].rosterTypes)
      ? value[0].rosterTypes
      : (value[0].rosterTypes as RosterTypeRecordResult[]).map(({ label }) => label);

    const mappedVotingPeriod = value[0].rosterCriteria.votingPeriod;

    const mappedShiftStatus = value[0].rosterCriteria.shiftStatus;

    return {
      name: 'roster',
      value: {
        electionId: mappedElectionId,
        electorateIds: mappedElectorateIds,
        rosterTypes: mappedRosterTypes,
        shiftStatus: mappedShiftStatus,
        votingPeriod: mappedVotingPeriod,
      },
    };
  },

  async fromFilter(filter: RosterFilter, { token }: ValidationContext) {
    const { electionId, electorateIds, votingPeriod, shiftStatus, rosterTypes } = filter.value;

    // This means it was originally null and got mapped to -1 by toFilter().
    // There is no situation where we should be mapping back again from that
    // (since electionId is a required field), so throw an error.
    if (electionId === -1) {
      throw new ListManagerFrontendError('RosterMapper.fromFilter() called with no election set');
    }

    let retrievedElection;
    let mappedElection;
    if (isTemplatePlaceholder(electionId)) {
      mappedElection = electionId;
    } else {
      retrievedElection = await getElectionById(token, electionId);
      mappedElection = { id: retrievedElection.id, label: retrievedElection.name };
    }

    let mappedElectorates;
    if (isTemplatePlaceholder(electorateIds)) {
      mappedElectorates = electorateIds;
    } else {
      if (isTemplatePlaceholder(electionId)) {
        // If election is a placeholder, we'll need to retrieve the electorates,
        // since we can't extract them from the election object
        const electorates = await getElectionElectoratesByIds(token, electorateIds);
        mappedElectorates = electorates.map(({ id, name }) => ({ id, label: name }));
      } else {
        if (!retrievedElection) {
          throw new Error('electorateIds is not a placeholder, but retrievedElection is undefined');
        }

        mappedElectorates = retrievedElection.electorates
          .filter((electorate) => electorateIds.includes(electorate.id))
          .map(({ id, name }) => ({ id, label: name }));
      }
    }

    const mappedRosterTypes = isTemplatePlaceholder(rosterTypes)
      ? rosterTypes
      : rosterTypes.map((label) => ROSTER_TYPES_WITH_IDS[label as RosterTypes]);

    const mappedShiftStatus = shiftStatus;
    const mappedVotingPeriod = votingPeriod;

    return {
      field: filter.name,
      operator: 'any in',
      value: [
        {
          election: mappedElection,
          electorates: mappedElectorates,
          rosterTypes: mappedRosterTypes,
          rosterCriteria: {
            votingPeriod: mappedVotingPeriod,
            shiftStatus: mappedShiftStatus,
          },
          errors: [],
        },
      ],
    };
  },
};
